import { createSelector } from 'reselect';

import { getFlight, getIsExchangeAutoMode } from '../../../../../Checkout/store/order/selectors';

import { AnyStateWithExchange } from '../types';
import { getSelectedFlightsInfo } from '../../lib/getSelectedFlightsInfo';
import { getFilteredByPricesFlights } from '../../lib/getFilteredByPricesFlights';
import { transformSelectedFlights } from '../../lib/transformSelectedFlights';
import { transformSelectedFareToFlight } from '../../lib/transformSelectedFareToFlight';
import { getCalculatedExchangePrices } from '../../lib/getCalculatedExchangePrices';
import { resolveExchangeTargetFlightIndex } from '../../lib/getExchangeTargetFlightIndex';
import { getSelectedSegments } from '../segments/selectors';

export const getExchangeVariantsState = (state: AnyStateWithExchange) => state.exchange.exchangeVariants;

export const getSelectedFlights = createSelector(getExchangeVariantsState, state => state.selectedFlights);

export const getExchangeVariantsFlightDirections = createSelector(
	getExchangeVariantsState,
	state => state.flightDirections
);

export const getExchangeVariantPrices = createSelector(
	getExchangeVariantsState,
	state => state.exchangeVariantPriceInfo
);

export const getSelectedFlightsCount = createSelector(getSelectedFlights, selectedFlights => selectedFlights.length);

export const getLastSelectedFlight = createSelector(
	getSelectedFlights,
	selectedFlights => selectedFlights[selectedFlights.length - 1]
);

/** [WEBSKY-8382, WEBSKY-8386]: Индекс рейса, по которому уходят ExchangePrice и CreateExchange. */
export const getExchangeTargetFlightIndex = createSelector(
	getSelectedFlights,
	getSelectedSegments,
	getExchangeVariantsFlightDirections,
	(selectedFlights, selectedSegments, flightDirections) =>
		resolveExchangeTargetFlightIndex(selectedFlights, selectedSegments.length, flightDirections)
);

/** [WEBSKY-8382, WEBSKY-8386]: Выбранный рейс для API обмена (первый или последний — в зависимости от RT-комбо). */
export const getExchangeTargetFlight = createSelector(
	getSelectedFlights,
	getExchangeTargetFlightIndex,
	(selectedFlights, targetFlightIndex) =>
		targetFlightIndex !== undefined ? selectedFlights[targetFlightIndex] : undefined
);

/** [WEBSKY-8382, WEBSKY-8386]: Id рейса для CreateExchange: из ответа ExchangePrice, если есть, иначе исходный выбор. */
export const getExchangeTargetFlightActualPriceId = createSelector(
	getExchangeTargetFlight,
	targetFlight => targetFlight?.actualPriceId || targetFlight?.priceId
);

export const getExchangeVariantsOnLeg = createSelector(
	getSelectedFlightsCount,
	getExchangeVariantsFlightDirections,
	(count, flightDirections) => flightDirections[count]?.legs || []
);

export const getSelectedExchangeVariants = createSelector(
	getSelectedFlights,
	getExchangeVariantsFlightDirections,
	transformSelectedFlights
);

export const getIsExchangeVariantsPricesEmpty = createSelector(
	getExchangeVariantPrices,
	exchangeVariantsPrices => !exchangeVariantsPrices.length
);

export const getIsAutoCreateExchangeOrder = createSelector(
	getIsExchangeVariantsPricesEmpty,
	getIsExchangeAutoMode,
	(isExchangeVariantsPricesEmpty, isAutoMode) => isExchangeVariantsPricesEmpty && !isAutoMode
);

export const getIsAutoReplaceExchangePrices = createSelector(
	getIsExchangeVariantsPricesEmpty,
	getIsExchangeAutoMode,
	(isExchangeVariantsPricesEmpty, isAutoMode) => isExchangeVariantsPricesEmpty && isAutoMode
);

export const getSelectedExchangeFlights = createSelector(
	getSelectedExchangeVariants,
	getFlight,
	(selectedVariants, flight) => {
		return selectedVariants.map(variant => transformSelectedFareToFlight(variant, flight.segmentGroups));
	}
);

export const getFilteredExchangeVariantsOnLeg = createSelector(
	getExchangeVariantsOnLeg,
	getSelectedExchangeVariants,
	getExchangeVariantPrices,
	getFilteredByPricesFlights
);

export const getExchangeSelectedFlightsInfo = createSelector(
	getIsExchangeAutoMode,
	getSelectedExchangeVariants,
	(isAutoMode, selectedExchangeVariants) => {
		if (isAutoMode) {
			return '';
		}

		return getSelectedFlightsInfo(selectedExchangeVariants);
	}
);

export const getSelectedExchangeVariantPrices = createSelector(
	getExchangeVariantPrices,
	getExchangeTargetFlightActualPriceId,
	getIsExchangeAutoMode,
	(prices, targetFlightActualPriceId, isAutoMode) => {
		if (!prices.length || !targetFlightActualPriceId || !isAutoMode) {
			return null;
		}

		return prices.find(price => price.flightId === targetFlightActualPriceId);
	}
);

export const getExchangePrices = createSelector(getSelectedExchangeVariantPrices, getCalculatedExchangePrices);
